/**
 * fresh-sign-in.ts — sign in ONLY after a guaranteed-clean session boundary.
 *
 * Tenant-isolation fix (context-isolation, 14 July 2026). Amplify v6 `signIn()`
 * throws `UserAlreadyAuthenticatedException` when a session already exists and
 * LEAVES THE PRIOR SESSION ACTIVE — it does not replace it. So signing in as a
 * second person on a browser whose previous user never signed out silently
 * keeps the FIRST user's host-scoped token cookie (ADR-092 D4). `/portal/me`
 * then runs under that stale token and the Level-1 context chooser renders the
 * PRIOR user's accounts — the observed leak where jeff@nexanomics.com's four
 * contexts (Nexanomics + Beta + Consultant + WIAD) appeared in what was meant
 * to be lucas.shin@cybercraft.net's modal.
 *
 * The server was never fooled — every account-scoped route enforces membership
 * independently (cedar-auth `authorizeRequest` → point-read on
 * `ACCOUNT#<accountId>/USER#<cognitoSub>`, no row → 403 `ACCOUNT_NOT_ACCESSIBLE`,
 * live-proven). The UI is not a security boundary. But a sign-in that can retain
 * a stranger's session is a real defect: it renders a control (a foreign
 * account door) the person is not entitled to, and mislabels it "current".
 *
 * Every real sign-in entry point (SignInPage, SignInEmbedPage, WelcomePage
 * activation) routes through here:
 *   1. signOutEveryZone()         — tear down every lingering session, in EVERY zone
 *   2. clearPortalSessionState()  — the chooser marker, context count, and any
 *                                   consent-gated impersonation session
 *   3. resolve the person's zone, point Amplify at it
 *   4. signIn(input)              — establish the NEW user's session, or fail cleanly
 *   5. announceIdentity(sub)      — every other open tab follows (tab-identity.ts)
 *
 * ⭐⭐ STEP 1 WAS `signOut()` UNTIL 31 OCTOBER 2026 — AND THAT ONLY EVER SIGNED OUT ONE ZONE.
 * Each zone has its own app client and its own token cookies; `signOut()` clears the configured
 * one. A session another tab held in the person's OTHER zone survived, and step 4 was refused
 * with "There is already a signed in user." (Q13 — reproduced both directions on the apex,
 * `e2e/cross-zone-signin.mjs`). See `zone-sessions.ts`.
 *
 * ⚠ THE SAME PERSON IS SIGNED IN AGAIN, NOT WAVED THROUGH. A leftover session that belongs to the
 * person now signing in is cleared like any other and they are signed straight back in with the
 * password they just typed — one step, no error. Reusing the leftover session instead would let
 * anyone at a shared browser "sign in" as the last person with any password at all.
 *
 * DemoPickerPage already did step 1 defensively for persona switches; this makes
 * the clean-boundary sign-in the single sanctioned path rather than a per-page
 * habit that three production entry points had missed.
 */
import { fetchAuthSession, signIn, signOut } from 'aws-amplify/auth';
import { clearPortalSessionState } from './session-state';
import { resolveZoneForEmail, type ZoneConfig } from './zone-resolution';
import { activeZone } from './zone-session';
import { signOutEveryZone, sweepCognitoCookies } from './zone-sessions';
import { announceIdentity } from './tab-identity';
import { isExistingSessionError } from './sign-in-errors';
import { configureAmplify } from '../amplify-config';

/**
 * Resolve `email`'s home zone and point BOTH halves of the portal at it — Amplify's pool and
 * every `/portal/*` origin (`activePortalApiBaseUrl()`). Returns the zone so a caller that must
 * make a pre-sign-in call can address that zone's API.
 *
 * ⭐⭐ EVERY PRE-SIGN-IN FLOW THAT ALREADY KNOWS THE PERSON'S EMAIL NEEDS THIS, NOT ONLY
 * SIGN-IN. Activation (`/welcome`) and password reset act on an identity in ONE zone's pool,
 * authenticated by ONE zone's secret — and until 23 September 2026 both ran against the build
 * zone unconditionally. A US customer's activation link was HMAC-checked with the AU secret
 * (each zone has its own) and refused as "Activation failed. Please contact support."; a US
 * customer's reset request went to the AU pool, which does not hold them, and no code ever
 * arrived. Sign-in alone had been given zone resolution, so the one US customer who could not
 * sign in was the one who had never been able to set a password in the first place.
 *
 * ⚠ IT CANNOT FAIL THE FLOW — `resolveZoneForEmail` swallows every error and returns the build
 * zone, so the worst case is the behaviour this portal had before runtime resolution existed.
 */
export async function enterZoneForEmail(email: string): Promise<ZoneConfig> {
  const zone = await resolveZoneForEmail(email);
  configureAmplify(zone);
  return zone;
}

/** Point Amplify at `username`'s zone (or the session's zone when there is no username). */
async function enterZone(username: string): Promise<void> {
  if (username) await enterZoneForEmail(username);
  else configureAmplify(activeZone());
}

/** Tell every other open tab who is now signed in. Best-effort: a failure here never fails sign-in. */
async function announceSignedIn(): Promise<void> {
  try {
    const session = await fetchAuthSession();
    const sub = session.tokens?.idToken?.payload?.sub;
    if (typeof sub === 'string' && sub) announceIdentity(sub);
  } catch {
    /* the other tabs still catch up on their next API call */
  }
}

/**
 * Sign in after discarding every pre-existing session, in every zone. `clearPortalSessionState()`
 * then runs unconditionally so no prior sign-in's context artefacts (count, chooser marker,
 * impersonation) can outlive the boundary. Signature mirrors Amplify's `signIn` so it is a
 * drop-in swap.
 */
export async function signInFresh(
  input: Parameters<typeof signIn>[0],
): ReturnType<typeof signIn> {
  await signOutEveryZone();
  clearPortalSessionState();

  // ⭐⭐ ADR-120 Amendment 4 — ZONE RESOLUTION HAPPENS HERE, IN THE ONE CHOKE POINT, AND
  // NOT IN THE SIGN-IN PAGES. This module already exists because three production entry
  // points (SignInPage, SignInEmbedPage, WelcomePage) each need the same thing done before
  // `signIn()`, and two of them had missed it. Resolving the zone is the identical problem
  // one step later: it must happen before EVERY sign-in, and adding it per-page would be
  // the same defect in a new coat. Putting it here means an entry point added tomorrow
  // gets it for free.
  //
  // ⚠ ORDER MATTERS THREE WAYS. It runs AFTER the sign-outs, so tearing down the previous
  // sessions cannot clear the zone we just resolved; and BEFORE `signIn()`, because Amplify
  // must already be pointed at the right pool when the credentials are presented.
  //
  // ⚠ AND IT CANNOT FAIL THE SIGN-IN. `resolveZoneForEmail` swallows every error and
  // returns the build zone, so this line's worst case is the behaviour this portal had
  // before runtime resolution existed. That is deliberate: this code sits on the live
  // login path, where the failure mode is "nobody can sign in".
  const username = typeof input.username === 'string' ? input.username : '';
  await enterZone(username);

  let result: Awaited<ReturnType<typeof signIn>>;
  try {
    result = await signIn(input);
  } catch (err) {
    // ⚠ ONE RETRY, ONLY FOR THE EXISTING-SESSION REFUSAL. Amplify raises it locally, before
    // Cognito is called, so the bot-check token has not been spent and the same input can be
    // presented again. Anything still holding a session after the sweep above (another tab
    // writing tokens back mid-sign-in) is cleared once more. A second refusal reaches the page,
    // which shows a friendly sentence — never Amplify's own message.
    if (!isExistingSessionError(err)) throw err;
    try {
      await signOut();
    } catch {
      /* cleared below regardless */
    }
    sweepCognitoCookies();
    await enterZone(username);
    result = await signIn(input);
  }
  if (result.nextStep.signInStep === 'DONE') await announceSignedIn();
  return result;
}
